/**
 * Hook tổng quát quản lý chọn hiển thị + thứ tự cột cho datasheet.
 * Tách logic chung; module-specific (Futures/Settings) cung cấp RTK Query hooks qua params.
 *
 * - menuOrder: thứ tự đầy đủ (gồm cả hidden) — giữ ổn định khi toggle visibility.
 * - hiddenSet: Set keys đang ẩn (toggle không đổi vị trí trong menu).
 * - visibleColKeys: derived, dùng cho table render + persist vào user_preferences.
 *
 * Persist 2 row trong public.user_preferences:
 *   - username = <current>: preference_value['<mode>_column'] = visible keys (theo user order)
 *   - username = 'default':  preference_value['<mode>_column'] = full key list (default order)
 */
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'

import {
  loadVisibleColumnKeys,
  normalizeColumnKeys,
  saveVisibleColumnKeys,
} from './storage'
import type { DatasheetColumnLike, UseDatasheetColumnsMenuResult } from './types'

export interface ColumnsPreferenceResponse {
  columns: string[] | null
  defaultColumns: string[] | null
}

export interface ColumnsPreferenceQueryResult {
  data?: ColumnsPreferenceResponse
  isSuccess: boolean
}

export interface ColumnsPreferencePutArg<TMode extends string> {
  mode: TMode
  columns: string[]
  defaultColumns?: string[]
}

interface MenuState {
  order: string[]
  hidden: Set<string>
}

/** Xây menuOrder + hiddenSet từ list visible đã lưu, giữ vị trí hidden theo static order. */
function buildMenuState(visibleKeys: string[], staticKeys: string[]): MenuState {
  const visibleSet = new Set(visibleKeys)
  const order: string[] = []
  const hidden = new Set<string>()
  let vi = 0
  for (const key of staticKeys) {
    if (visibleSet.has(key)) {
      order.push(visibleKeys[vi] ?? key)
      vi += 1
    } else {
      order.push(key)
      hidden.add(key)
    }
  }
  return { order, hidden }
}

export interface UseDatasheetColumnsParams<TCol extends DatasheetColumnLike, TMode extends string> {
  mode: TMode
  /** Static column definitions theo thứ tự gốc (default order). */
  allColumns: TCol[]
  /** localStorage key — giữ visible-cols qua reload trước khi backend trả về. */
  storageKey: string
  /** RTK Query result của GET preference theo mode (caller phải gọi hook tương ứng). */
  preferenceQuery: ColumnsPreferenceQueryResult
  /** Trigger PUT preference. */
  putPreference: (arg: ColumnsPreferencePutArg<TMode>) => { unwrap: () => Promise<unknown> }
}

export function useDatasheetColumns<TCol extends DatasheetColumnLike, TMode extends string>(
  params: UseDatasheetColumnsParams<TCol, TMode>,
): UseDatasheetColumnsMenuResult<TCol> {
  const { mode, allColumns, storageKey, preferenceQuery, putPreference } = params
  const { data: columnPref, isSuccess: columnPrefReady } = preferenceQuery

  const allKeys = useMemo(() => allColumns.map((c) => String(c.key)), [allColumns])
  const columnsByKey = useMemo(
    () => new Map(allColumns.map((c) => [String(c.key), c] as const)),
    [allColumns],
  )

  const [menuOrder, setMenuOrder] = useState<string[]>(() => {
    const saved = loadVisibleColumnKeys(storageKey, allKeys)
    return buildMenuState(saved, allKeys).order
  })
  const [hiddenSet, setHiddenSet] = useState<Set<string>>(() => {
    const saved = loadVisibleColumnKeys(storageKey, allKeys)
    return buildMenuState(saved, allKeys).hidden
  })

  const visibleColKeys = useMemo(
    () => menuOrder.filter((k) => !hiddenSet.has(k)),
    [menuOrder, hiddenSet],
  )

  useEffect(() => {
    const saved = loadVisibleColumnKeys(storageKey, allKeys)
    const { order, hidden } = buildMenuState(saved, allKeys)
    setMenuOrder(order)
    setHiddenSet(hidden)
  }, [allKeys, storageKey])

  // Ref tới putPreference + mode để effect schema-change không phụ thuộc
  // reference inline-callback bất ổn từ caller (tránh infinite re-render).
  const putPreferenceRef = useRef(putPreference)
  const modeRef = useRef(mode)
  useEffect(() => {
    putPreferenceRef.current = putPreference
    modeRef.current = mode
  })

  // Đánh dấu đã reset schema cho 1 key-set cụ thể để không reset lặp.
  const schemaResetForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!columnPrefReady || !columnPref?.columns) return

    // Schema-change detection: nếu `defaultColumns` lưu trên server khác
    // `allKeys` hiện tại (dev đã đổi thứ tự / thêm / bớt cột), prefs cũ đã
    // stale → reset về default mới + persist lại để các lần sau đồng bộ.
    const savedDefaults = columnPref.defaultColumns
    if (savedDefaults && savedDefaults.length > 0) {
      const sameSchema =
        savedDefaults.length === allKeys.length &&
        savedDefaults.every((k, i) => k === allKeys[i])
      if (!sameSchema) {
        const allKeysSig = allKeys.join('|')
        if (schemaResetForRef.current === allKeysSig) return
        schemaResetForRef.current = allKeysSig

        const { order, hidden } = buildMenuState(allKeys, allKeys)
        setMenuOrder(order)
        setHiddenSet(hidden)
        saveVisibleColumnKeys(storageKey, allKeys)
        void putPreferenceRef
          .current({ mode: modeRef.current, columns: allKeys, defaultColumns: allKeys })
          .unwrap()
          .catch(() => undefined)
        return
      }
    }

    const next = normalizeColumnKeys(columnPref.columns, allKeys)
    if (next.length === 0) return
    const { order, hidden } = buildMenuState(next, allKeys)
    setMenuOrder(order)
    setHiddenSet(hidden)
    saveVisibleColumnKeys(storageKey, next)
  }, [
    allKeys,
    columnPref?.columns,
    columnPref?.defaultColumns,
    columnPrefReady,
    storageKey,
  ])

  const persist = useCallback(
    (keys: string[]) => {
      saveVisibleColumnKeys(storageKey, keys)
      void putPreference({ mode, columns: keys, defaultColumns: allKeys })
        .unwrap()
        .catch(() => undefined)
    },
    [allKeys, mode, putPreference, storageKey],
  )

  const toggleColumn = useCallback(
    (key: string) => {
      setHiddenSet((prev) => {
        const next = new Set(prev)
        if (next.has(key)) {
          next.delete(key)
        } else {
          const visibleCount = menuOrder.length - prev.size
          if (visibleCount <= 1) return prev
          next.add(key)
        }
        const newVisible = menuOrder.filter((k) => !next.has(k))
        persist(newVisible)
        return next
      })
    },
    [menuOrder, persist],
  )

  const resetToDefault = useCallback(() => {
    const defaultOrder = normalizeColumnKeys(columnPref?.defaultColumns ?? [], allKeys)
    const source = defaultOrder.length > 0 ? defaultOrder : allKeys
    setMenuOrder([...source])
    setHiddenSet(new Set())
    persist([...source])
  }, [allKeys, columnPref?.defaultColumns, persist])

  const [dragKey, setDragKey] = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const onDragStart = useCallback(
    (key: string) => (e: DragEvent<HTMLElement>) => {
      setDragKey(key)
      e.dataTransfer.effectAllowed = 'move'
      // Firefox cần setData để drag thực sự kích hoạt.
      e.dataTransfer.setData('text/plain', key)
    },
    [],
  )

  const onDragOver = useCallback(
    (key: string) => (e: DragEvent<HTMLElement>) => {
      if (!dragKey || dragKey === key) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverKey(key)
    },
    [dragKey],
  )

  const onDrop = useCallback(
    (targetKey: string) => (e: DragEvent<HTMLElement>) => {
      e.preventDefault()
      const sourceKey = dragKey
      setDragKey(null)
      setDragOverKey(null)
      if (!sourceKey || sourceKey === targetKey) return
      setMenuOrder((prev) => {
        const from = prev.indexOf(sourceKey)
        const to = prev.indexOf(targetKey)
        if (from < 0 || to < 0) return prev
        const next = [...prev]
        const [item] = next.splice(from, 1)
        next.splice(to, 0, item)
        const newVisible = next.filter((k) => !hiddenSet.has(k))
        persist(newVisible)
        return next
      })
    },
    [dragKey, hiddenSet, persist],
  )

  const onDragEnd = useCallback(() => {
    setDragKey(null)
    setDragOverKey(null)
  }, [])

  const visibleColumns = useMemo(
    () => visibleColKeys.flatMap((k) => {
      const col = columnsByKey.get(k)
      return col ? [col] : []
    }),
    [columnsByKey, visibleColKeys],
  )

  const menuItems = useMemo(
    () => menuOrder.flatMap((k) => {
      const col = columnsByKey.get(k)
      return col ? [col] : []
    }),
    [columnsByKey, menuOrder],
  )

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const openMenu = useCallback((anchor: HTMLElement) => setMenuAnchor(anchor), [])
  const closeMenu = useCallback(() => setMenuAnchor(null), [])

  return {
    visibleColumns,
    menuItems,
    hiddenSet,
    menuAnchor,
    openMenu,
    closeMenu,
    toggleColumn,
    resetToDefault,
    dragKey,
    dragOverKey,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
  }
}
